import React from 'react';
import { BarChart2, PlusCircle, MinusCircle, Activity } from 'lucide-react';

/**
 * Компонент для настройки распределения случайных чисел
 */
const DistributionSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  handleWeightedValueChange,
  addWeightedValue,
  removeWeightedValue
}) => {
  // Если не выбрано специальное распределение, не отображаем секцию
  if (formData.distribution === 'uniform') {
    return null;
  }

  return (
    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 mb-6 border border-indigo-100 dark:border-indigo-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
        Настройки {formData.distribution === 'normal' ? 'нормального' : 'взвешенного'} распределения
      </h3>
      
      {/* Для нормального распределения */}
      {formData.distribution === 'normal' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Среднее значение */}
          <div>
            <label htmlFor="normalMean" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Activity className="w-4 h-4 mr-2 text-indigo-500" />
              Среднее значение
            </label>
            <input
              type="text"
              id="normalMean"
              name="normalMean"
              value={formData.normalMean}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.normalMean ? 'input-error' : ''}`}
              placeholder="Например: 50"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Центр нормального распределения
            </p>
            {errors.normalMean && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.normalMean}
              </p>
            )}
          </div>

          {/* Стандартное отклонение */}
          <div>
            <label htmlFor="normalDeviation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Стандартное отклонение
            </label>
            <input
              type="text"
              id="normalDeviation"
              name="normalDeviation"
              value={formData.normalDeviation}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.normalDeviation ? 'input-error' : ''}`}
              placeholder="Например: 15"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Определяет разброс значений вокруг среднего
            </p>
            {errors.normalDeviation && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.normalDeviation}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Для взвешенного распределения */}
      {formData.distribution === 'weighted' && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Настройка весов для значений
            </div>
            <button
              type="button"
              onClick={addWeightedValue}
              className="p-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
              title="Добавить значение"
            >
              <PlusCircle className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-2 mb-4">
            {formData.weightedValues.map((item, index) => (
              <div key={`weighted-${index}`} className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Значение</label>
                  <input
                    type="text"
                    value={item.value}
                    onChange={(e) => handleWeightedValueChange(index, 'value', e.target.value)}
                    className="form-input w-full"
                    placeholder="Значение"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Вес</label>
                  <input
                    type="text"
                    value={item.weight}
                    onChange={(e) => handleWeightedValueChange(index, 'weight', e.target.value)}
                    className="form-input w-full"
                    placeholder="Вес"
                  />
                </div>
                {formData.weightedValues.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeWeightedValue(index)}
                    className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 mt-4"
                    title="Удалить значение"
                  >
                    <MinusCircle className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Чем выше вес значения, тем чаще оно будет встречаться в результатах. 
            Например, значение с весом 5 будет встречаться примерно в 5 раз чаще, чем значение с весом 1.
          </p>
        </div>
      )}

      {/* Информация о выбранном распределении */}
      <div className="mt-4 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {formData.distribution === 'normal' ? (
            <>
              <span className="font-medium">Нормальное распределение</span> подразумевает, что большинство сгенерированных чисел будут близки к среднему значению, 
              а чем дальше от среднего, тем реже встречаются числа. Это распределение часто встречается в природе и науке.
            </>
          ) : (
            <>
              <span className="font-medium">Взвешенное распределение</span> позволяет задать вероятность появления конкретных значений. 
              Частота появления каждого значения будет пропорциональна его весу.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default DistributionSection;